import { FC, MouseEvent, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import ListSubheader from "@mui/material/ListSubheader";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowUpRightFromSquare,
  faChartColumn,
  faChartLine,
  faEarthAmericas,
  faLock,
  faSparkles,
  faUsersGear,
} from "@fortawesome/pro-regular-svg-icons";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";
import { getUserClaims } from "../../utils/auth";
import { BRAND_COLORS } from "./constants";
import {
  adminHubLimited,
  canSeeAdminHub,
  canSeeDataStudio,
  dataStudioNotPurchased,
  isV2User,
  tenantPurchased,
} from "../../utils/rbac";
import { getAppUrl, type AppId } from "../../utils/module-urls";

interface AppDescriptor {
  id: AppId;
  label: string;
  subtitle: string;
  icon: IconDefinition;
  /** The org has not purchased this module: shown, not navigable (RGI-2609). */
  locked?: boolean;
  /** Tooltip on the lock icon; defaults to "Upgrade". */
  lockedReason?: string;
}

const DATA_STUDIO_NOT_PURCHASED = "Data Studio is not included in your plan";

const APPS: Record<AppId, AppDescriptor> = {
  admin_hub: {
    id: "admin_hub",
    label: "Admin Hub",
    subtitle: "Settings & users",
    icon: faUsersGear,
  },
  market_analyzer: {
    id: "market_analyzer",
    label: "Market Analyzer",
    subtitle: "Trends & insights",
    icon: faEarthAmericas,
  },
  data_studio: {
    id: "data_studio",
    label: "Data Studio",
    subtitle: "Predictive Scoring Models",
    icon: faChartColumn,
  },
  sales_copilot: {
    id: "sales_copilot",
    label: "Sales Copilot",
    subtitle: "AI-assisted deals",
    icon: faChartLine,
  },
  ai_sales_copilot: {
    id: "ai_sales_copilot",
    label: "AI Sales Copilot",
    subtitle: "Offerings, Account AI & Workflows",
    icon: faSparkles,
  },
};

const MODULE_ORDER: Exclude<AppId, "admin_hub">[] = [
  "market_analyzer",
  "data_studio",
  "sales_copilot",
  "ai_sales_copilot",
];

type CopilotAppId = "sales_copilot" | "ai_sales_copilot";

/** The copilots in the order they win a tie, as in mk-admin-app's rbac.ts. */
const COPILOT_APPS: CopilotAppId[] = ["sales_copilot", "ai_sales_copilot"];

function isCopilotApp(id: AppId): id is CopilotAppId {
  return (COPILOT_APPS as AppId[]).includes(id);
}

/** Resolves the current tenant id from the access token, mirroring navigation-config. */
function getTenantIdFromToken(): string | null {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return null;
    const decoded = jwtDecode<{ mkClaims?: { tenantId?: string } }>(token);
    return decoded.mkClaims?.tenantId ?? null;
  } catch {
    return null;
  }
}

/**
 * v2 RBAC topbar app switcher. Shows Admin Hub + every module the user has
 * been assigned. Selecting another app navigates via `window.location.href`.
 * The current app (Market Analyzer here) is highlighted and is a no-op on click.
 *
 * Hidden entirely for v1 users (no `mkClaims.rbac` claim on JWT).
 */
export const AppSwitcher: FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const claims = getUserClaims();
  if (!isV2User(claims.rbac)) return null;

  const tenantId = getTenantIdFromToken();
  const current = APPS.market_analyzer;
  const isSuperKudu = !!claims.superKudu;
  const assigned = new Set<string>(claims.rbac.assignedModules);
  const flags = { superKudu: isSuperKudu };

  // Admin Hub shows for Org Admins or Data Studio customers; Data Studio shows
  // for Org Admins or module holders of a tenant that purchased it, and greyed
  // out when the tenant did not (RGI-3158); read-only seats never see it.
  // Market Analyzer and the copilots require the module to be assigned.
  // SuperKudu sees all.
  //
  // A module the org has not purchased is listed as locked rather than hidden,
  // so an MA-only tenant sees what else exists (RGI-2609).
  //
  // The copilots follow mk-admin-app's `resolveApps`: a holder of both is
  // offered one (MSI wins, RGI-2287), and a tenant that bought neither sees a
  // single locked entry under the name it is sold as today.
  const copilot = COPILOT_APPS.find((m) => assigned.has(m)) ?? null;
  const anyCopilotPurchased = COPILOT_APPS.some((m) =>
    tenantPurchased(claims.rbac, m, flags)
  );
  const apps: AppDescriptor[] = [
    ...(canSeeAdminHub(claims.rbac, flags) ? [APPS.admin_hub] : []),
    ...MODULE_ORDER.flatMap((m): AppDescriptor[] => {
      if (m === current.id) return [APPS[m]];
      if (isCopilotApp(m)) {
        if (!anyCopilotPurchased) {
          return m === "ai_sales_copilot" ? [{ ...APPS[m], locked: true }] : [];
        }
        return isSuperKudu || m === copilot ? [APPS[m]] : [];
      }
      if (m === "data_studio") {
        if (dataStudioNotPurchased(claims.rbac, flags)) {
          return [
            {
              ...APPS[m],
              locked: true,
              lockedReason: DATA_STUDIO_NOT_PURCHASED,
            },
          ];
        }
        return canSeeDataStudio(claims.rbac, flags) ? [APPS[m]] : [];
      }
      if (!tenantPurchased(claims.rbac, m, flags)) {
        return [{ ...APPS[m], locked: true }];
      }
      return isSuperKudu || assigned.has(m) ? [APPS[m]] : [];
    }),
  ];

  // "(limited access)": Data Studio standard users get a limited Admin nav.
  const limitedApps: Partial<Record<AppId, boolean>> = {
    admin_hub: adminHubLimited(claims.rbac, flags),
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (app: AppDescriptor) => {
    if (app.locked) return;
    handleClose();
    if (app.id === current.id) return;
    const url = getAppUrl(app.id, { tenantId });
    if (url) window.location.href = url;
  };

  const handleOpenInNewTab = (url: string) => {
    handleClose();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {/* An outlined button naming the current module, as the 2026 rebrand
          mockups draw it: prussian border, label and caret. */}
      <Button
        id="topbar-app-switcher-trigger"
        onClick={handleOpen}
        aria-controls={open ? "topbar-app-switcher-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        endIcon={
          <FontAwesomeIcon
            icon={faCaretDown}
            style={{ width: 12, height: 12, display: "block" }}
          />
        }
        sx={{
          height: 44,
          px: "12px",
          borderRadius: "10px",
          border: `1px solid ${BRAND_COLORS.PRUSSIAN}`,
          color: BRAND_COLORS.PRUSSIAN,
          backgroundColor: open ? "#f3f4f6" : "transparent",
          fontSize: "20px",
          fontWeight: 500,
          lineHeight: 1,
          textTransform: "none",
          "&:hover": { backgroundColor: "#f3f4f6" },
          "& .MuiButton-endIcon": { ml: "8px" },
        }}
      >
        {current.label}
        {limitedApps[current.id] && (
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 400,
              fontStyle: "italic",
              color: "#9ca3af",
              ml: "4px",
            }}
          >
            (limited access)
          </Box>
        )}
      </Button>
      <Menu
        id="topbar-app-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        slotProps={{
          paper: {
            sx: {
              marginTop: "8px",
              width: 260,
              padding: "4px",
              boxShadow:
                "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
            },
          },
        }}
      >
        <ListSubheader
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6b7280",
            padding: "6px 8px 8px",
            lineHeight: 1,
            backgroundColor: "transparent",
          }}
        >
          Select Application
        </ListSubheader>
        {apps.map((app) => {
          const isCurrent = app.id === current.id;
          const url =
            isCurrent || app.locked ? null : getAppUrl(app.id, { tenantId });
          return (
            <MenuItem
              key={app.id}
              onClick={() => handleSelect(app)}
              disabled={app.locked}
              aria-disabled={app.locked}
              title={app.locked ? app.lockedReason : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: isCurrent ? "#eaf2ff" : "transparent",
                "&:hover": {
                  backgroundColor: isCurrent ? "#eaf2ff" : "#f3f4f6",
                },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: isCurrent ? "#2563eb" : "#f3f4f6",
                  color: isCurrent ? "#ffffff" : "#4b5563",
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={app.icon} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: isCurrent ? "#2563eb" : "#1f1f1f",
                    lineHeight: 1.2,
                  }}
                >
                  {app.label}
                  {limitedApps[app.id] && !app.locked && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        fontStyle: "italic",
                        color: "#9ca3af",
                        ml: "4px",
                      }}
                    >
                      (limited access)
                    </Box>
                  )}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: "#6b7280", lineHeight: 1.2 }}
                >
                  {app.subtitle}
                </Typography>
              </Box>
              {app.locked && (
                <Tooltip title={app.lockedReason ?? "Upgrade"} placement="top">
                  <Box
                    component="span"
                    aria-label={
                      app.lockedReason ?? `Upgrade to unlock ${app.label}`
                    }
                    sx={{
                      marginLeft: "auto",
                      display: "inline-flex",
                      color: "#9ca3af",
                      // A disabled MenuItem swallows pointer events, so the
                      // icon opts back in to keep the tooltip reachable.
                      pointerEvents: "auto",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLock}
                      style={{ width: 14, height: 14 }}
                    />
                  </Box>
                </Tooltip>
              )}
              {url && (
                <IconButton
                  size="small"
                  aria-label={`Open ${app.label} in a new tab`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenInNewTab(url);
                  }}
                  sx={{
                    marginLeft: "auto",
                    color: "#9ca3af",
                    "&:hover": { color: "#4b5563" },
                  }}
                >
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    style={{ width: 14, height: 14 }}
                  />
                </IconButton>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
